import React, { useState, useMemo, useRef } from 'react';
import { Loader2, X, Plus, Sparkles, Save, Search, GripVertical } from 'lucide-react';
import {
  getSkillsConfig,
  saveSkillsConfig,
  generateSkillsForTenant,
  getSkillsCounts,
  type SkillsConfigEntry,
} from '../../services/api';
import { useAdminData } from './AdminDataContext';
import { AdminSelect } from './AdminSelect';

type SkillsConfig = Record<string, SkillsConfigEntry>;

const TOP_LIMIT = 15;

function autoFill(top: string[], all: string[], excluded: Set<string>): string[] {
  if (top.length >= TOP_LIMIT) return top;
  const topSet = new Set(top);
  const pool = all.filter((s) => !topSet.has(s) && !excluded.has(s));
  return [...top, ...pool.slice(0, TOP_LIMIT - top.length)];
}

function TopSkillsList({
  skills,
  all,
  excluded,
  counts,
  onRemove,
  onChange,
}: {
  skills: string[];
  all: string[];
  excluded: Set<string>;
  counts: Record<string, number>;
  onRemove: (skill: string) => void;
  onChange: (s: string[]) => void;
}) {
  const dragIdx = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  function handleDragStart(e: React.DragEvent, i: number) {
    dragIdx.current = i;
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e: React.DragEvent, i: number) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(i);
  }

  function handleDrop(e: React.DragEvent, i: number) {
    e.preventDefault();
    const from = dragIdx.current;
    if (from === null || from === i) { setDragOver(null); return; }
    const copy = [...skills];
    const [moved] = copy.splice(from, 1);
    copy.splice(i, 0, moved);
    onChange(copy);
    dragIdx.current = null;
    setDragOver(null);
  }

  function handleDragEnd() {
    dragIdx.current = null;
    setDragOver(null);
  }

  return (
    <div className="space-y-1">
      {skills.length === 0 && (
        <p className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">No top skills yet. Add from All Skills.</p>
      )}
      {skills.map((skill, i) => (
        <div
          key={skill}
          draggable
          onDragStart={(e) => handleDragStart(e, i)}
          onDragOver={(e) => handleDragOver(e, i)}
          onDrop={(e) => handleDrop(e, i)}
          onDragEnd={handleDragEnd}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border group cursor-grab active:cursor-grabbing transition-colors
            ${dragOver === i
              ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
            }`}
        >
          <GripVertical className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" />
          <span className="flex-1 text-sm text-gray-800 dark:text-gray-200 truncate">{skill}</span>
          {counts[skill] !== undefined && (
            <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0 tabular-nums">{counts[skill]}</span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(skill); }}
            className="p-0.5 text-gray-300 dark:text-gray-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            title="Remove from top"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

function AllSkillsList({
  all,
  top,
  counts,
  onAddToTop,
}: {
  all: string[];
  top: string[];
  counts: Record<string, number>;
  onAddToTop: (skill: string) => void;
}) {
  const [query, setQuery] = useState('');
  const topSet = new Set(top);
  const filtered = useMemo(
    () => all.filter((s) => s.toLowerCase().includes(query.toLowerCase())),
    [all, query],
  );

  return (
    <div className="flex flex-col h-full">
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search skills…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="overflow-y-auto flex-1 space-y-0.5 pr-1">
        {filtered.length === 0 && (
          <p className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">No skills match.</p>
        )}
        {filtered.map((skill) => {
          const inTop = topSet.has(skill);
          return (
            <div
              key={skill}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 group"
            >
              <span className={`flex-1 text-sm truncate ${inTop ? 'text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-200'}`}>
                {skill}
              </span>
              {counts[skill] !== undefined && (
                <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0 tabular-nums">{counts[skill]}</span>
              )}
              {inTop ? (
                <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">in top</span>
              ) : (
                <button
                  onClick={() => onAddToTop(skill)}
                  className="p-0.5 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  title="Add to top"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SkillsSection() {
  const { tenants } = useAdminData();
  const [tenantId, setTenantId] = useState<number | ''>('');
  const [domainKey, setDomainKey] = useState('');
  const [activeField, setActiveField] = useState('_default');
  const [config, setConfig] = useState<SkillsConfig | null>(null);
  const [counts, setCounts] = useState<Record<string, Record<string, number>>>({});
  const [excludedByField, setExcludedByField] = useState<Record<string, Set<string>>>({});
  const [noSavedConfig, setNoSavedConfig] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const selectedTenant = tenants.find((t) => t.id === tenantId);
  const domainOptions: string[] = useMemo(() => {
    if (!selectedTenant?.config) return [];
    return Object.keys(selectedTenant.config as Record<string, unknown>);
  }, [selectedTenant]);

  const fieldTabs: string[] = useMemo(() => {
    if (!config) return [];
    return Object.keys(config).sort((a, b) => (a === '_default' ? -1 : b === '_default' ? 1 : a.localeCompare(b)));
  }, [config]);

  const currentEntry: SkillsConfigEntry = config?.[activeField] ?? { top: [], all: [] };

  function updateCurrentEntry(entry: SkillsConfigEntry) {
    setConfig((prev) => prev ? { ...prev, [activeField]: entry } : prev);
  }

  async function handleLoad() {
    if (!tenantId || !domainKey) return;
    setLoading(true);
    setError('');
    setConfig(null);
    setNoSavedConfig(false);
    setSaveSuccess(false);
    try {
      const res = await getSkillsConfig(Number(tenantId), domainKey);
      if (res.skillsConfig) {
        setConfig(res.skillsConfig);
        setExcludedByField({});
        setActiveField('_default');
        try { setCounts(await getSkillsCounts(Number(tenantId), domainKey)); } catch { setCounts({}); }
      } else {
        setNoSavedConfig(true);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerate() {
    if (!tenantId || !domainKey) return;
    setGenerating(true);
    setError('');
    setNoSavedConfig(false);
    setSaveSuccess(false);
    try {
      const res = await generateSkillsForTenant(Number(tenantId), domainKey);
      const seeded = Object.fromEntries(
        Object.entries(res.config).map(([k, v]) => [k, { ...v, top: autoFill(v.top, v.all, new Set()) }])
      );
      setConfig(seeded);
      setCounts(res.counts);
      setExcludedByField({});
      setActiveField('_default');
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to generate');
    } finally {
      setGenerating(false);
    }
  }

  async function handleSave() {
    if (!tenantId || !domainKey || !config) return;
    setSaving(true);
    setError('');
    setSaveSuccess(false);
    try {
      await saveSkillsConfig(Number(tenantId), domainKey, config);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Skills</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
          Curate top skills shown to customers per tenant and domain.
        </p>
      </div>

      {/* Selectors */}
      <div className="flex items-end gap-3 mb-6 flex-wrap">
        <div className="flex flex-col gap-1 min-w-[200px]">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Tenant</label>
          <AdminSelect
            value={tenantId === '' ? '' : String(tenantId)}
            onChange={(val) => { setTenantId(val ? Number(val) : ''); setDomainKey(''); setConfig(null); setNoSavedConfig(false); }}
            options={tenants.filter((t) => t.isActive).map((t) => ({ value: String(t.id), label: t.name || t.domain }))}
            placeholder="Select tenant…"
            searchable
          />
        </div>

        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Domain</label>
          <AdminSelect
            value={domainKey}
            onChange={(val) => { setDomainKey(val); setConfig(null); setNoSavedConfig(false); }}
            options={domainOptions.map((d) => ({ value: d, label: d }))}
            placeholder="Select domain…"
            disabled={!tenantId || domainOptions.length === 0}
          />
        </div>

        <button
          onClick={handleLoad}
          disabled={!tenantId || !domainKey || loading}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load Saved'}
        </button>

        <button
          onClick={handleGenerate}
          disabled={!tenantId || !domainKey || generating}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Generate Fresh
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Editor */}
      {config && (
        <>
          {/* Field tabs */}
          {fieldTabs.length > 1 && (
            <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
              {fieldTabs.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveField(f)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeField === f
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {f === '_default' ? 'All (Default)' : f}
                </button>
              ))}
            </div>
          )}

          {/* Two-panel editor */}
          <div className="grid grid-cols-2 gap-6">
            {/* Top Skills */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col" style={{ maxHeight: 520 }}>
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Top Skills</h3>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Shown first in filter pills. Order matters.</p>
                </div>
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                  {currentEntry.top.length}
                </span>
              </div>
              <div className="overflow-y-auto flex-1 p-3">
                <TopSkillsList
                  skills={currentEntry.top}
                  all={currentEntry.all}
                  excluded={excludedByField[activeField] ?? new Set()}
                  counts={counts[activeField] ?? {}}
                  onRemove={(skill) => {
                    const exc = new Set(Array.from(excludedByField[activeField] ?? []).concat(skill));
                    setExcludedByField((prev) => ({ ...prev, [activeField]: exc }));
                    const next = currentEntry.top.filter((s) => s !== skill);
                    updateCurrentEntry({ ...currentEntry, top: autoFill(next, currentEntry.all, exc) });
                  }}
                  onChange={(top) => updateCurrentEntry({ ...currentEntry, top })}
                />
              </div>
            </div>

            {/* All Skills */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col" style={{ maxHeight: 520 }}>
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">All Skills</h3>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Full list available in skill search.</p>
                </div>
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                  {currentEntry.all.length}
                </span>
              </div>
              <div className="flex-1 overflow-hidden p-3 flex flex-col">
                <AllSkillsList
                  all={currentEntry.all}
                  top={currentEntry.top}
                  counts={counts[activeField] ?? {}}
                  onAddToTop={(skill) => {
                    if (!currentEntry.top.includes(skill)) {
                      setExcludedByField((prev) => {
                        const exc = new Set(Array.from(prev[activeField] ?? []));
                        exc.delete(skill);
                        return { ...prev, [activeField]: exc };
                      });
                      updateCurrentEntry({ ...currentEntry, top: [...currentEntry.top, skill] });
                    }
                  }}
                />
              </div>
            </div>
          </div>

        </>
      )}

      {config && (
        <div className="mt-6 flex flex-col items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-8 py-2.5 text-sm font-medium rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save All Fields
          </button>
          {saveSuccess && (
            <span className="text-sm text-green-600 dark:text-green-400 font-medium">All fields saved successfully.</span>
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500">Saves skills for all fields at once.</p>
        </div>
      )}

      {noSavedConfig && !config && !loading && !generating && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-6">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">No custom config saved for this tenant + domain.</p>
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Customers currently see hardcoded default skills. Click <strong>Generate Fresh</strong> to build a config from actual candidate data, then curate and save it.
          </p>
        </div>
      )}

      {!noSavedConfig && !config && !loading && !generating && tenantId && domainKey && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-16 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Click <strong>Load Saved</strong> to view existing config or <strong>Generate Fresh</strong> to pull from Elasticsearch.
          </p>
        </div>
      )}

      {!config && !loading && !generating && (!tenantId || !domainKey) && (
        <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-16 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">Select a tenant and domain to get started.</p>
        </div>
      )}
    </div>
  );
}
